import type { RoleSlug } from "@/5-shared/constants/content/types";
import type { Audience } from "@/5-shared/types";

/**
 * Single source of truth for every internal link on the site. Never hardcode a
 * path string in a `to`/`href`/`navigate()`, add or reference an entry here.
 * `scripts/check-links.mjs` (`npm run lint:links`) enforces this.
 */
export const PATHS = {
  home: "/",
  companies: "/company",
  companiesProduct: "/company/product",
  companiesSolutions: "/company/solutions",
  producers: "/producer",
  producersProduct: "/producer/product",
  capital: "/capital",
  explorer: "/resources/dec-explorer",
  faq: "/resources/faq",
  about: "/resources/about-us",
  contact: "/contact-form",
  blog: "/resources/blog",
  privacyPolicy: "/privacy-policy.html",
} as const;

/** The landing's logical path (`""` when a bad URL is served the home markup). */
export const isLandingPath = (path: string): boolean => path === PATHS.home || path === "";

/**
 * Pages that bring their own chrome instead of the site's SiteNav/Footer/MobileCTA:
 * the landing, and the DEC Capital page (its own sticky header, an audience apart).
 * One pure place, so the chrome decision cannot drift between callers (a double
 * navbar once surfaced when a bad URL was served the home markup).
 */
export const ownsChrome = (path: string): boolean => isLandingPath(path) || path === PATHS.capital;

/** Overview path for an audience (companies → /company, producers → /producer). */
export const audiencePath = (a: Audience): string =>
  a === "producers" ? PATHS.producers : PATHS.companies;

type ProducerRole = { slug: RoleSlug; path: string; label: string; navLabel: string; desc: string; seoDesc: string };

const role = (slug: RoleSlug, label: string, path: string, desc: string, seoDesc: string): ProducerRole => ({
  slug,
  path,
  label,
  navLabel: `Per ${label}`,
  desc,
  seoDesc,
});

export const PRODUCER_ROLES: ProducerRole[] = [
  role(
    "ipp-light",
    "Piccoli produttori",
    "/producer/ipp-light",
    "Liquidità immediata, zero complessità",
    "Liquidità immediata sui tuoi impianti fotovoltaici, senza la complessità di un PPA: DEC Energy contrattualizza la produzione e tu mantieni la proprietà.",
  ),
  role(
    "ipp",
    "IPP",
    "/producer/ipps",
    "Cassa anticipata, portafoglio più grande",
    "Cassa anticipata sui flussi futuri per far crescere il portafoglio impianti, mantenendo la proprietà degli asset già costruiti.",
  ),
  role(
    "utility",
    "Utility",
    "/producer/utility",
    "Transizione mantenendo la proprietà",
    "Accelera la transizione mantenendo la proprietà degli impianti: DEC Energy monetizza oggi i flussi di cassa futuri del tuo parco rinnovabile.",
  ),
];
